import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import {
  Box,
  Button,
  Icon,
  Loader,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import AccessRequestHistory from '../../../components/AccessRequestHistory';
import { Cards } from '../../../components/CertsDetailsList';
import { CertsGlobalError } from '../../../components/Common';
import ReviewAndSubmit from '../../../components/ReviewAndSubmit';
import {
  TAB_STATUS_ALL,
  TAB_STATUS_CONS,
  TAB_STATUS_DONE,
  TAB_STATUS_TODO,
} from '../../../components/ViewControls';
import {
  getCertDetailsData,
  setAlteastOneIsSelected,
  setBulkFooterShow,
  setCertsTabs,
  setCertTriggerFromSearchOrFilter,
  setCheckForBulkAction,
  setColumnsToShow,
  setIndeterminateForBulkAction,
  setIsAtLeastThreeActions,
  setSelectedRowFilters,
  setSidePanelVisible,
  setTriggerClearBtnForFilters,
  setTriggerClearBtnForHierarchy,
  setTriggerClearBtnForSearch,
  // setTriggerFromApplyActions,
  triggerFilterChange,
} from '../../../redux/certdetails/action';
import {
  CAMPAIGN_TYPES,
  CAMPAIGNTYPE_ENTITLEMENTOWNER,
  ITEMS_API_OFFSET,
  SCREEN_SIZE_MOBILE_XLARGE,
  SCREEN_WIDTH_QUERIES,
} from '../../../utils/constant';
import { useInvertedLocalize } from '../../../utils/localization';
import {
  certsFormattedMessage,
  executeBackNavigation,
  flattenList,
  getMessageKey,
} from '../../../utils/utils';
import CertsExport from '../CertsExport/CertsExport';

import { invertedMessages } from './messages';
import {
  addActionsTracker,
  addEOActionsTracker,
  addTrackerObject,
  getColumnNamesToShow,
  mergeRecordsUtil,
  modifyTreeForSearchOrFilter,
} from './utils';

import './CertsDetailsList.css';

function CertsUnAuthorizedModal() {
  const l = useInvertedLocalize(invertedMessages);

  return (
    <ModalIsland
      className='CertsDetailsList-unAuthorizedError'
      kind='critical'
      alertTitle={l('Access Restricted')}
      alertSubtitle={l(
        'The user does not have sufficient privileges to access certification.'
      )}
      isOpen
      onClose={() => {
        window.location.replace('/ECMv6/request/requestHome');
      }}
      primaryButton={
        <Button
          type='button'
          kind='filled'
          isEnabled
          size='medium'
          onClick={() => {
            window.location.replace('/ECMv6/request/requestHome');
          }}>
          {l('Back To Home Page')}
        </Button>
      }
      secondaryButton={<div />}
    />
  );
}

function CertsDetailsList({
  isMoreDataAvailable,
  setIsMoreDataAvailable,
  showFilterBar,
  detailsListRef,
  setSidePanelData,
  mergedLists,
  setMergedLists,
  currentOffset,
  setCurrentOffset,
  mergedListsAfterRowFilter,
  setMergedListsAfterRowFilter,
  handleAutoSelectParent,
}) {
  const intl = useIntl();
  const dispatch = useDispatch();

  // Data
  const [isScrolling, setIsScrolling] = useState(false);
  // const [foundAutoSelectTrackers, setFoundAutoSelectTrackers] = useState(null);
  const [actionPopoverOpenId, setActionPopoverOpenId] = useState(null);
  const [prevActiveTab, setPrevActiveTab] = useState(null);
  const observerTarget = useRef(null);
  const isProcessingRef = useRef(false);
  /** Prevents no-data flash during transitions */
  const isTransitioningRef = useRef(false);

  const {
    bulkAction: {
      bulkFooterInfo,
      isActionBySelectAll: isBulkActionBySelectAll,
      isChecked: isBulkSelectChecked,
      isIndeterminate: isBulkSelectIndeterminate,
    },
    core: {
      certDetailsLists,
      itemsOffSetCount,
      columnNames,
      isLoading,
      isViewApiError,
      isViewApiUnauthorizedError,
      searchText,
      totalItems,
      campaignType,
    },
    global: {
      backNavigation,
      certKey,
      columnPickerList,
      isGlobalError,
      mediaQueryValue,
      viewRowsList,
    },
    lineActions: { isAtLeastThreeActions },
    reviewAndSubmit: { primaryCertifier, shouldShowReviewAndSubmitButton },
    saviCompanion: { isSaviApplied },
    tabs: { tabData },
    viewControls: {
      advancedFilters,
      dynamicSortFilter: { sortDirection, sortByColumn },
      isSmartApplied,
      isTriggeredFromSearchOrFilter,
      appliedRowFilters,
      groupByOptions,
    },
  } = useSelector((state) => state?.certifications?.details);
  const { messages } = useSelector((state) => state?.user);

  const activeTab = tabData.find((tab) => tab.isActive);
  const consultedTab = tabData.find((tab) => tab.key === TAB_STATUS_CONS);
  const allTab = tabData.find((tab) => tab.key === TAB_STATUS_ALL);
  const hasActiveTabDetails = activeTab?.count > 0;

  const isEOCampaign =
    CAMPAIGN_TYPES[campaignType] === CAMPAIGNTYPE_ENTITLEMENTOWNER;
  const detailsListClasses = classnames(
    'CertsDetailsList',
    showFilterBar && 'CertsDetailsList--showFilterBar',
    isLoading && 'CertsDetailsList--isLoading',
    isMoreDataAvailable && 'CertsDetailsList--isMoreDataAvailable'
  );

  const isAnyError = Boolean(isGlobalError || isViewApiError);
  const hasNoCertsResultsToDisplay = Boolean(
    mergedLists && mergedLists?.length === 0
  );
  const hasNoAdvancedFiltersResults = Boolean(advancedFilters?.length > 0);
  const hasNoSearchResults = Boolean(searchText?.trim().length > 0);

  const mobileXlargeQuery = SCREEN_WIDTH_QUERIES.find(
    (item) => item.label === SCREEN_SIZE_MOBILE_XLARGE
  );
  const isMobileWidth = mediaQueryValue >= mobileXlargeQuery.value;

  useEffect(() => {
    if (
      !mergedListsAfterRowFilter ||
      mergedListsAfterRowFilter?.length === 0 ||
      isAtLeastThreeActions
    )
      return;

    const checkActionsInFirstTenNodes = (nodes) => {
      const flattenedList = flattenList(nodes);
      const nodesToCheck = flattenedList.slice(0, 10);

      const hasThreeOrMoreActions = nodesToCheck.some((node) => {
        const actionKeys = Object.keys(node?.availableAction || {});

        return actionKeys.length >= 3;
      });

      if (hasThreeOrMoreActions) dispatch(setIsAtLeastThreeActions(true));
    };

    checkActionsInFirstTenNodes(mergedListsAfterRowFilter);
  }, [mergedListsAfterRowFilter]);

  // eslint-disable-next-line consistent-return
  useEffect(() => {
    const listElement = detailsListRef.current;

    if (listElement) {
      const handleScroll = () => {
        /**
         * Handles the scroll event on the list element.
         * Sets the `isScrolling` state to true when scrolling starts.
         * Clears any existing scroll timeout to prevent multiple timeouts from being set.
         * Sets a timeout to reset the `isScrolling` state to false after 1 second of no scrolling.
         */
        setIsScrolling(true);
        clearTimeout(window.scrollTimeout);
        window.scrollTimeout = setTimeout(() => {
          setIsScrolling(false);
        }, 1000);
      };

      listElement.addEventListener('scroll', handleScroll);

      return () => {
        listElement.removeEventListener('scroll', handleScroll);
      };
    }
  }, [detailsListRef, certDetailsLists]);

  // Tell the controls handler that the absolute bottom is attainable
  useEffect(() => {
    if (currentOffset > totalItems) return setIsMoreDataAvailable(false);

    return setIsMoreDataAvailable(true);
  }, [currentOffset, totalItems]);

  // TODO: Commented out at Umeshes request, add or remove based on user feedback
  // useEffect(() => {
  //   if (verifyParentMap?.length === 0) return;

  //   const trackers = [];

  //   verifyParentMap
  //     .filter((criteria) => !criteria.childname)
  //     .forEach((criteria) => {
  //       const tracker = findTracker(mergedListsAfterRowFilter, criteria);

  //       if (tracker) {
  //         trackers.push({
  //           ...tracker,
  //           type: criteria.type,
  //         });
  //       }
  //     });

  //   // setFoundAutoSelectTrackers(trackers);
  // }, [verifyParentMap]);

  /**
   * Handles the display of the side panel with the provided node data.
   *
   * @param {Object} nodeData - The data of the node to be displayed in the side panel.
   * @param {Object} nodeData.tracker - The tracker object containing the node's tracking information.
   * @param {string} nodeData.tracker.id - The unique identifier (tracker ID) of the node.
   * @param {number} nodeData.tracker.level - The level of the node in the hierarchy.
   * @param {boolean} nodeData.tracker.isConnected - Indicates if the node is connected.
   * @param {boolean} nodeData.tracker.isChecked - Indicates if the node is checked.
   * @param {boolean} nodeData.tracker.isIndeterminate - Indicates if the node is in an indeterminate state.
   * @param {Array} nodeData.children - The children nodes of the current node.
   *
   * @returns {void} - This function does not return a value.
   */
  const handleSidePanel = useCallback((nodeData) => {
    // Open the sidepanel
    dispatch(setSidePanelVisible(true));

    // Setting the data for the sidepanel
    setSidePanelData(nodeData);
  });

  /**
   * Merges new data into an existing list (merged list) and updates the UI state accordingly.
   *
   * @param {Array} existingList - The current list of items.
   * @param {Array} newList - The new list of items to be merged.
   * @returns {void} This function does not return a value. It updates the state of the merged list.
   */
  const mergeResponsesUI = (existingList, newList) => {
    const mergedResponse = mergeRecordsUtil(
      isTriggeredFromSearchOrFilter || itemsOffSetCount === 0
        ? []
        : existingList,
      newList,
      campaignType,
      appliedRowFilters,
      groupByOptions
    );

    const responseAfterTrackerObj = addTrackerObject(
      isEOCampaign,
      mergedResponse
    );

    const responseAfterActionStatus = isEOCampaign
      ? addEOActionsTracker(responseAfterTrackerObj)
      : addActionsTracker(responseAfterTrackerObj);

    setMergedLists(responseAfterActionStatus);
  };

  // As part of CR-19183, resetting offset and list on `clear filter`
  useEffect(() => {
    if (itemsOffSetCount === 0) {
      /** Suppress no-data flash on clear */
      isTransitioningRef.current = true;
      setMergedLists([]);
      setCurrentOffset(0);
    }
  }, [itemsOffSetCount]);

  useEffect(() => {
    if (columnPickerList) {
      const columnNamesToShow = getColumnNamesToShow(columnPickerList);

      dispatch(setColumnsToShow(columnNamesToShow));
    }
  }, [columnPickerList, dispatch]);

  useEffect(() => {
    if (certKey) {
      if (columnNames && typeof columnNames === 'string') {
        dispatch(setCertTriggerFromSearchOrFilter(false));
        dispatch(
          getCertDetailsData(
            certKey,
            columnNames,
            ITEMS_API_OFFSET,
            currentOffset,
            searchText
          )
        );
        setCurrentOffset((prevOffset) => prevOffset + ITEMS_API_OFFSET);
      }
    }
  }, [certKey, columnNames, dispatch]);

  useEffect(() => {
    if (isTriggeredFromSearchOrFilter) {
      /** Suppress no-data flash on filter */
      isTransitioningRef.current = true;
      setCurrentOffset(ITEMS_API_OFFSET); // Already started from zero in search
      setMergedLists([]);
    }
  }, [isTriggeredFromSearchOrFilter]);

  useEffect(() => {
    if (activeTab.key !== prevActiveTab) {
      /** Suppress no-data flash on tab */
      isTransitioningRef.current = true;
      setCurrentOffset(0);
      setMergedLists([]);
    }

    setPrevActiveTab(activeTab.key);

    // Get the items API response for the current active tab
    if (certKey && columnNames && typeof columnNames === 'string') {
      setCurrentOffset(ITEMS_API_OFFSET);
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          ITEMS_API_OFFSET,
          0,
          searchText
        )
      );
    }
  }, [activeTab?.key]);

  useEffect(() => {
    if (certDetailsLists) {
      /** Data arrived, allow no-data view */
      isTransitioningRef.current = false;
      mergeResponsesUI(mergedLists, certDetailsLists);
    }
  }, [certDetailsLists]);

  useEffect(() => {
    if (sortByColumn && sortDirection) {
      /** Suppress no-data flash on sort */
      isTransitioningRef.current = true;
      dispatch(setCertTriggerFromSearchOrFilter(true));
      setCurrentOffset(0);
      setMergedLists([]);
      dispatch(
        getCertDetailsData(
          certKey,
          columnNames,
          ITEMS_API_OFFSET,
          0,
          searchText
        )
      );
    }
  }, [sortByColumn, sortDirection]);

  useEffect(() => {
    const tempMergedListsAfterRowFilter =
      modifyTreeForSearchOrFilter(mergedLists);

    const flattenedList = flattenList(tempMergedListsAfterRowFilter);
    const listofCheckStatus = flattenedList.map(
      (node) => node?.tracker?.isChecked
    );

    const listStatusCount = listofCheckStatus.length;
    const isEveryItemChecked =
      !listofCheckStatus.some((value) => !value) &&
      listStatusCount > 0 &&
      (listStatusCount >= activeTab?.count ||
        bulkFooterInfo?.totalSelectedItems === totalItems);
    const isAtleastOneItemChecked =
      listofCheckStatus.some((value) => value) && listStatusCount > 0;

    setMergedListsAfterRowFilter(tempMergedListsAfterRowFilter);

    if (tempMergedListsAfterRowFilter?.length === 0) {
      dispatch(setBulkFooterShow(false));

      return;
    }

    if (isBulkActionBySelectAll) return;

    if (isBulkSelectChecked) {
      if (isEveryItemChecked) {
        dispatch(setIndeterminateForBulkAction(false));
        dispatch(setAlteastOneIsSelected(true));
        dispatch(setCheckForBulkAction(true));
        dispatch(setBulkFooterShow(true));

        return;
      }

      if (isAtleastOneItemChecked) {
        dispatch(setAlteastOneIsSelected(true));
        dispatch(setIndeterminateForBulkAction(true));
        dispatch(setCheckForBulkAction(false));
        dispatch(setBulkFooterShow(true));

        return;
      }

      dispatch(setAlteastOneIsSelected(false));
      dispatch(setIndeterminateForBulkAction(false));
      dispatch(setCheckForBulkAction(false));
      dispatch(setBulkFooterShow(false));

      return;
    }

    if (!isBulkSelectChecked) {
      if (isEveryItemChecked) {
        dispatch(setIndeterminateForBulkAction(false));
        dispatch(setCheckForBulkAction(true));
        dispatch(setAlteastOneIsSelected(true));
        dispatch(setBulkFooterShow(true));

        return;
      }

      if (isAtleastOneItemChecked) {
        dispatch(setIndeterminateForBulkAction(true));
        dispatch(setCheckForBulkAction(false));
        dispatch(setAlteastOneIsSelected(true));
        dispatch(setBulkFooterShow(true));

        return;
      }

      dispatch(setAlteastOneIsSelected(false));
      dispatch(setIndeterminateForBulkAction(false));
      dispatch(setCheckForBulkAction(false));
      dispatch(setBulkFooterShow(false));
    }
  }, [mergedLists, dispatch]);

  useEffect(() => {
    if (isLoading) return;

    if (hasActiveTabDetails) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting && !isProcessingRef.current) {
            if (currentOffset < totalItems) {
              isProcessingRef.current = true;
              dispatch(setCertTriggerFromSearchOrFilter(false));

              if (currentOffset !== 0) {
                dispatch(
                  getCertDetailsData(
                    certKey,
                    columnNames,
                    ITEMS_API_OFFSET,
                    currentOffset,
                    searchText
                  )
                );
              }

              setCurrentOffset((prevOffset) => prevOffset + ITEMS_API_OFFSET);

              // Reset flag after a short delay to prevent rapid successive calls
              setTimeout(() => {
                isProcessingRef.current = false;
              }, 100);
            }
          }
        },
        { threshold: 0, rootMargin: '100px' }
      );

      if (observerTarget.current) {
        observer.observe(observerTarget.current);
      }

      // eslint-disable-next-line consistent-return
      return () => {
        if (observerTarget.current) {
          observer.unobserve(observerTarget.current);
        }

        isProcessingRef.current = false;
      };
    }
  }, [observerTarget, isLoading, currentOffset]);

  /**
   * Recursively updates the `isChecked` and `isIndeterminate` properties of the `tracker` object
   * for each item in the list and its children based on the provided flag.
   *
   * @param {Array} prev - The previous list of items to be updated.
   * @param {boolean} flag - The flag indicating whether the items should be checked or not.
   * @returns {Array} The updated list of items with the `isChecked` and `isIndeterminate` properties updated.
   */
  const handleBulkActionCheck = useCallback(
    (prev, flag) =>
      prev.map((item) => ({
        ...item,
        tracker: {
          ...item.tracker,
          isChecked: flag,
          isIndeterminate: false,
        },
        children:
          item?.children?.length > 0
            ? handleBulkActionCheck(item.children, flag)
            : [],
      })),
    []
  );

  useEffect(() => {
    if (isBulkActionBySelectAll && !isBulkSelectIndeterminate) {
      setMergedLists((prevMergedLists) =>
        handleBulkActionCheck(prevMergedLists, isBulkSelectChecked)
      );
    }
  }, [isBulkSelectChecked, isBulkSelectIndeterminate]);

  /**
   * Resets the filters for the "View All" (Removes filter for Hierarchy filter) button and updates the UI accordingly.
   * This function dispatches actions to update the certification filters and sets the active tab to "All".
   *
   * @returns {void} This function does not return a value. It updates the state and UI based on the filter reset.
   */
  const resetFilterForViewAllButton = () => {
    dispatch(setTriggerClearBtnForHierarchy(true));

    const makeAllTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_ALL,
      isHighlighted: false,
    }));

    dispatch(setCertsTabs(makeAllTabActive));

    if (activeTab?.key === TAB_STATUS_ALL) {
      /** Suppress no-data flash on reset */
      isTransitioningRef.current = true;
      setMergedLists([]);
      setCurrentOffset(ITEMS_API_OFFSET);
      dispatch(setSelectedRowFilters(viewRowsList));
      dispatch(triggerFilterChange());
    }
  };

  const renderNoDataView = ({
    title,
    body,
    buttonText,
    buttonAction,
    isReadyToSubmit,
    buttonIcon = null,
  }) => {
    const getEmptyButton = () => {
      if (buttonText && buttonAction)
        return (
          <Button
            type='button'
            kind='filled'
            size={isMobileWidth ? 'medium' : 'large'}
            onClick={buttonAction}
            leftIcon={
              buttonIcon ? (
                <Icon kind={buttonIcon} size='medium' color='neutral-100' />
              ) : null
            }>
            {buttonText}
          </Button>
        );

      if (isReadyToSubmit) return <ReviewAndSubmit buttonSize='large' />;

      return null;
    };

    return (
      <Box tag='section' className='CertificationDetails-emptyCenter'>
        <Box tag='div' className='CertificationDetails-emptyOrError'>
          <Typography kind='h1'>{title}</Typography>
          <Typography kind='body2'>{body}</Typography>
          {getEmptyButton()}
        </Box>
      </Box>
    );
  };

  const getNoDataContent = () => {
    // Suppress empty-state messages during transitions to prevent flashing.
    // - isTransitioningRef: covers the gap AFTER setMergedLists([]) but before API data arrives
    // - isTabSwitching: covers the render BEFORE the tab-switch effect runs
    //   (Redux updates activeTab immediately, but the effect that clears lists runs after render)
    // - isPendingFilterTransition: covers the render BEFORE the search/filter effect runs
    //   Only active while old data is still present (mergedLists not yet cleared by the effect).
    //   Once the effect clears mergedLists, isTransitioningRef takes over until API responds.
    const isTabSwitching =
      prevActiveTab !== null && activeTab.key !== prevActiveTab;
    const isPendingFilterTransition =
      isTriggeredFromSearchOrFilter && mergedLists?.length > 0;

    if (
      isTransitioningRef.current ||
      isTabSwitching ||
      isPendingFilterTransition
    )
      return null;

    const isZeroToDoItems =
      !hasActiveTabDetails && activeTab.key === TAB_STATUS_TODO;
    const isZeroDoneItems =
      !hasActiveTabDetails && activeTab.key === TAB_STATUS_DONE;
    const isZeroConsultedItems =
      !hasActiveTabDetails && activeTab.key === TAB_STATUS_CONS;
    const hasZeroCountForConsultedItems = consultedTab.count === 0;
    const hasZeroAllTabCount = allTab?.count === 0;

    if (!isLoading && hasZeroAllTabCount) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_CAUGHT_UP_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_CAUGHT_UP_TITLE')),
        body:
          messages.CERTS_DETAILS_CAUGHT_UP_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_CAUGHT_UP_BODY')),
        buttonText:
          messages.CERTS_DETAILS_CAUGHT_UP_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_CAUGHT_UP_BUTTON')),
        buttonAction: () => executeBackNavigation(backNavigation),
        isReadyToSubmit: false,
      });
    }

    if (!isLoading && hasNoSearchResults && hasNoCertsResultsToDisplay) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_NO_MATCH_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_TITLE')),
        body:
          messages.CERTS_DETAILS_NO_MATCH_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_BODY')),
        buttonText:
          messages.CERTS_DETAILS_NO_MATCH_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_NO_MATCH_BUTTON')),
        buttonAction: () => dispatch(setTriggerClearBtnForSearch(true)),
        isReadyToSubmit: false,
      });
    }

    if (
      !isLoading &&
      (hasNoAdvancedFiltersResults || isSaviApplied || isSmartApplied) &&
      hasNoCertsResultsToDisplay
    ) {
      const filterTypeText =
        activeTab?.key === TAB_STATUS_TODO
          ? certsFormattedMessage(
              messages.CERTS_DETAILS_CLEAR_FILTER_BODY_1 ||
                localize(
                  intl,
                  getMessageKey('CERTS_DETAILS_CLEAR_FILTER_BODY_1')
                ),
              {
                count: activeTab?.count || 0,
              }
            )
          : messages.CERTS_DETAILS_CLEAR_FILTER_BODY_2 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_BODY_2'));

      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_CLEAR_FILTER_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_TITLE')),
        body: filterTypeText,
        buttonText:
          messages.CERTS_DETAILS_CLEAR_FILTER_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_CLEAR_FILTER_BUTTON')),
        buttonAction: () => dispatch(setTriggerClearBtnForFilters(true)),
        isReadyToSubmit: false,
      });
    }

    if (
      !isLoading &&
      isZeroToDoItems &&
      hasZeroCountForConsultedItems &&
      shouldShowReviewAndSubmitButton
    ) {
      return renderNoDataView({
        title: primaryCertifier
          ? messages.CERTS_DETAILS_CERT_READY_TITLE_1 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_TITLE_1'))
          : messages.CERTS_DETAILS_CERT_READY_TITLE_2 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_TITLE_2')),
        body: primaryCertifier
          ? messages.CERTS_DETAILS_CERT_READY_BODY_1 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_BODY_1'))
          : messages.CERTS_DETAILS_CERT_READY_BODY_2 ||
            localize(intl, getMessageKey('CERTS_DETAILS_CERT_READY_BODY_2')),
        buttonText: '',
        buttonAction: () => {},
        isReadyToSubmit: true,
      });
    }

    const makeConsultedTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_CONS,
      isHighlighted: false,
    }));

    if (!isLoading && isZeroToDoItems && shouldShowReviewAndSubmitButton) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_PEND_CONSULT_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_PEND_CONSULT_TITLE')),
        body:
          messages.CERTS_DETAILS_PEND_CONSULT_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_PEND_CONSULT_BODY')),
        buttonText:
          messages.CERTS_DETAILS_PEND_CONSULT_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_PEND_CONSULT_BUTTON')),
        buttonAction: () => dispatch(setCertsTabs(makeConsultedTabActive)),
        isReadyToSubmit: false,
      });
    }

    if (
      !isLoading &&
      isZeroToDoItems &&
      !shouldShowReviewAndSubmitButton &&
      !backNavigation?.consulteeLoggedIn
    ) {
      const title =
        messages.CERTS_DETAILS_ALL_DONE_TITLE ||
        localize(intl, getMessageKey('CERTS_DETAILS_ALL_DONE_TITLE'));
      const body =
        messages.CERTS_DETAILS_ALL_DONE_BODY ||
        localize(intl, getMessageKey('CERTS_DETAILS_ALL_DONE_BODY'));

      return (
        <Box tag='section' className='CertificationDetails-emptyCenter'>
          <Box tag='div' className='CertificationDetails-emptyOrError'>
            <Typography kind='h1'>{title}</Typography>
            <Typography kind='body2'>{body}</Typography>
            <CertsExport />
          </Box>
        </Box>
      );
    }

    const makeTodoTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_TODO,
      isHighlighted: false,
    }));

    if (!isLoading && isZeroDoneItems) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_NOT_CERTIFIED_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_TITLE')),
        body:
          messages.CERTS_DETAILS_NOT_CERTIFIED_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_BODY')),
        buttonText:
          messages.CERTS_DETAILS_NOT_CERTIFIED_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_BUTTON')),
        buttonAction: () => dispatch(setCertsTabs(makeTodoTabActive)),
        isReadyToSubmit: false,
      });
    }

    const makeDoneTabActive = tabData.map((tab) => ({
      ...tab,
      isActive: tab.key === TAB_STATUS_DONE,
      isHighlighted: false,
    }));

    if (consultedTab.isActive && consultedTab.count === 0) {
      dispatch(setCertsTabs(makeDoneTabActive));
    }

    if (!isLoading && isZeroConsultedItems) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_MOV_CONSULT_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_MOV_CONSULT_TITLE')),
        body:
          messages.CERTS_DETAILS_MOV_CONSULT_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_MOV_CONSULT_BODY')),
        buttonText:
          messages.CERTS_DETAILS_NOT_CERTIFIED_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_NOT_CERTIFIED_BUTTON')),
        buttonAction: () => dispatch(setCertsTabs(makeTodoTabActive)),
        isReadyToSubmit: false,
      });
    }

    if (!isLoading && hasNoCertsResultsToDisplay) {
      return renderNoDataView({
        title:
          messages.CERTS_DETAILS_ALL_LIST_TITLE ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_TITLE')),
        body:
          messages.CERTS_DETAILS_ALL_LIST_BODY ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_BODY')),
        buttonText:
          messages.CERTS_DETAILS_ALL_LIST_BUTTON ||
          localize(intl, getMessageKey('CERTS_DETAILS_ALL_LIST_BUTTON')),
        buttonAction: resetFilterForViewAllButton,
        isReadyToSubmit: false,
      });
    }

    return null;
  };

  if (isViewApiUnauthorizedError) return <CertsUnAuthorizedModal />;
  if (isAnyError) return <CertsGlobalError />;

  return (
    <section
      id='certsDetailsList'
      ref={detailsListRef}
      className={detailsListClasses}
      data-testid='cert-details-list'>
      {getNoDataContent() || (
        <>
          <Cards
            data={mergedListsAfterRowFilter}
            handleSidePanel={handleSidePanel}
            handleAutoSelectParent={handleAutoSelectParent}
            setMergedLists={setMergedLists}
            actionPopoverOpenId={actionPopoverOpenId}
            setActionPopoverOpenId={setActionPopoverOpenId}
            isScrolling={isScrolling}
          />
          <AccessRequestHistory />
        </>
      )}
      <div className='CertsDetailsList-targetLoader' ref={observerTarget} />
      {isLoading && (
        <Box tag='div' className='CertsDetailsList-loader'>
          <Loader kind='loop' format='main' color='brand' size='large' />
        </Box>
      )}
    </section>
  );
}

CertsDetailsList.propTypes = {
  isMoreDataAvailable: PropTypes.bool.isRequired,
  setIsMoreDataAvailable: PropTypes.func.isRequired,
  showFilterBar: PropTypes.bool.isRequired,
  detailsListRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  ]),
  setSidePanelData: PropTypes.func.isRequired,
  mergedLists: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setMergedLists: PropTypes.func.isRequired,
  currentOffset: PropTypes.number.isRequired,
  setCurrentOffset: PropTypes.func.isRequired,
  mergedListsAfterRowFilter: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  setMergedListsAfterRowFilter: PropTypes.func.isRequired,
  handleAutoSelectParent: PropTypes.func.isRequired,
};

CertsDetailsList.defaultProps = { detailsListRef: null };

export default CertsDetailsList;
